import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLRDirectGate } from "@/contexts/LRDirectGateContext";
import { supabase } from "@/integrations/supabase/client";
import { logAuthEvent } from "@/lib/debug/authEventLog";
import { useChatSystem } from "@/hooks/useChatSystem";
import { useChatContext } from "@/contexts/ChatContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link, useLocation, useNavigate } from "react-router-dom";
import lanerunnerLogo from "@/assets/lanerunner-logo-black-transparent.png";
import {
  Settings, PlusCircle, Search, ChevronDown, LogOut, User, Sparkles, MessageSquare, Building2, Truck, Info, GraduationCap, Shield, FileText, RefreshCcw, Cookie, ShieldAlert, Menu, LifeBuoy, Loader2, Users, CreditCard,
} from "lucide-react";
import { HubSettingsDialog } from "@/components/social/HubSettingsDialog";



import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { CompanyCreditsBadge } from "@/components/dashboard/CompanyCreditsBadge";
import { VerifiedBadge } from "@/components/layout/VerifiedBadge";
import { useCompanyAccount } from "@/hooks/useCompanyAccount";
import { UserAvatar } from "@/components/common/UserAvatar";
import { DemoHint } from "@/components/demo/DemoHint";
import { useGuide } from "@/contexts/GuideContext";
import { useCarrierOffers } from "@/hooks/useCarrierOffers";
import { useUnreadWins } from "@/hooks/useUnreadWins";
import { useUnreadLRDirectLoads, markLRDirectLoadsRead } from "@/hooks/useUnreadLRDirectLoads";
import { useUnreadQuoteRequests } from "@/hooks/useUnreadQuoteRequests";
import { useUnreadQuoteResponses } from "@/hooks/useUnreadQuoteResponses";
import { useDirectBidAlerts } from "@/hooks/useDirectBidAlerts";
import { useShipperMyLoadsCount } from "@/hooks/useShipperMyLoadsCount";
import { useUnreadJoinRequests } from "@/hooks/useUnreadJoinRequests";
import { useUnreadJoinRequestResponses } from "@/hooks/useUnreadJoinRequestResponses";
import { useProductContext, navBucketForHref } from "@/contexts/ProductContext";
import { SupportTicketModal } from "@/components/support/SupportTicketModal";
import { ProductContextSwitcher } from "@/components/layout/ProductContextSwitcher";


const NAV_ITEMS = [
  { label: "Dashboard",      href: "/broker-dashboard", hintKey: "nav.dashboard" },
  { label: "Search Loads",   href: "/search",           hintKey: "nav.searchLoads" },
  { label: "Search Trucks",  href: "/search-trucks",    hintKey: "nav.searchTrucks" },
  { label: "My Loads",       href: "/my-loads",          hintKey: "nav.myLoads" },
  { label: "My Trucks",      href: "/my-trucks",         hintKey: "nav.myTrucks" },
  { label: "My Offers",      href: "/search?tab=my-offers", hintKey: "nav.myOffers" },
  { label: "Quick Post",     href: "/post-load",         hintKey: "nav.quickPost" },
  { label: "Post Truck",     href: "/post-truck",        hintKey: "nav.postTruck" },
  { label: "ATLAS Dispatcher", href: "/dispatcher",      hintKey: "nav.dispatcher" },
  { label: "Atlas Hub",      href: "/atlas-hub",         hintKey: "nav.atlasHub" },
  { label: "Atlas Hub",      href: "/atlas-hub-carrier", hintKey: "nav.atlasHub" },
  { label: "LR Direct",      href: "/lr-direct",          hintKey: "nav.lrDirect" },
  { label: "My Loads",        href: "/lr-direct/my-loads", hintKey: "nav.lrDirectMyLoads" },
  { label: "My Loads",       href: "/lr-direct/my-wins",  hintKey: "nav.lrDirectMyWins" },
  { label: "LR Direct Post", href: "/lr-direct/post/start",     hintKey: "nav.lrDirectPost" },
  { label: "ATLAS",          href: "/lr-direct/atlas",    hintKey: "nav.lrDirectAtlas" },
  { label: "My Quotes",      href: "/lr-direct/my-quotes",       hintKey: "nav.lrDirectQuotesShipper" },
  { label: "Quote Requests", href: "/lr-direct/broker-quotes",  hintKey: "nav.lrDirectQuotesBroker" },
  
  { label: "My Network",     href: "/lr-direct/my-network", hintKey: "nav.lrDirectShippers" },
  { label: "My Network",     href: "/lr-direct/network-settings", hintKey: "nav.lrDirectNetwork" },
  { label: "Freight Lab",    href: "/atlas-lab",          hintKey: "nav.freightLab" },
  { label: "LR Hub",        href: "/social",              hintKey: "nav.lrHub" },
];

const TAB_ROLES: { tab: string; roles: ("Broker" | "Carrier" | "Shipper" | "All")[] }[] = [
  { tab: "Dashboard",      roles: ["All"] },
  { tab: "Search Loads",   roles: ["Broker", "Carrier"] },
  { tab: "Search Trucks",  roles: ["Broker", "Carrier", "Shipper"] },
  { tab: "My Loads",       roles: ["Broker", "Shipper"] },
  { tab: "My Trucks",      roles: ["Carrier"] },
  { tab: "My Offers",      roles: ["Carrier"] },
  { tab: "Quick Post",     roles: ["Broker", "Shipper"] },
  { tab: "Post Truck",     roles: ["Carrier"] },
  { tab: "Atlas Hub",      roles: ["Broker"] },
  { tab: "LR Direct",      roles: ["Broker", "Shipper"] },
  { tab: "My Loads",       roles: ["Broker", "Shipper"] },
  { tab: "My Loads",       roles: ["Broker"] },
  { tab: "LR Direct Post", roles: ["Shipper"] },
  { tab: "ATLAS",          roles: ["Shipper"] },
  
  { tab: "My Network",     roles: ["Broker"] },
  { tab: "My Network",     roles: ["Shipper"] },
  { tab: "Freight Lab",    roles: ["All"] },
  { tab: "LR Hub",         roles: ["All"] },
];

// Per-role allow-list of visible tab hrefs. Admin and demo users bypass.
const ROLE_TABS: Record<"broker" | "carrier" | "shipper" | "broker_carrier" | "unknown", string[]> = {
  broker: [
    "/broker-dashboard", "/lr-direct/broker", "/search", "/search-trucks", "/my-loads",
    "/post-load", "/atlas-hub", "/lr-direct", "/lr-direct/board", "/lr-direct/my-wins", "/lr-direct/broker-quotes", "/lr-direct/my-network", "/atlas-lab", "/social",
  ],
  carrier: [
    // LaneRunner-side tabs (unchanged).
    "/carrier-dashboard", "/search", "/my-trucks",
    "/post-truck", "/dispatcher", "/atlas-hub-carrier", "/atlas-lab", "/social",
    // LR Direct tabs — same set brokers get (Stage 2). Route access is
    // already open; bidding stays billing-gated.
    "/lr-direct/broker", "/lr-direct", "/lr-direct/board", "/lr-direct/my-wins",
    "/lr-direct/broker-quotes", "/lr-direct/my-network",
  ],
  shipper: [
    "/shipper-dashboard", "/shipper-dashboard-lanerunner", "/search", "/search-trucks", "/my-loads",
    "/post-load", "/atlas-hub", "/lr-direct", "/lr-direct/board", "/lr-direct/post", "/lr-direct/post/start", "/lr-direct/atlas", "/lr-direct/my-loads", "/lr-direct/my-quotes", "/lr-direct/network-settings",
    "/atlas-lab", "/social",
  ],
  broker_carrier: [
    "/broker-dashboard", "/lr-direct/broker", "/search", "/search-trucks", "/my-loads", "/my-trucks",
    "/search?tab=my-offers",
    "/post-load", "/post-truck", "/dispatcher", "/atlas-hub", "/lr-direct", "/lr-direct/board", "/lr-direct/my-wins", "/lr-direct/broker-quotes", "/lr-direct/my-network", "/atlas-lab", "/social",
  ],
  // Safe fallback when profile.user_type is missing/unknown — universal tabs only.
  unknown: [
    "/broker-dashboard", "/search", "/search-trucks",
    "/atlas-hub", "/atlas-lab", "/social",
  ],
};

const ROLE_COLORS: Record<string, string> = {
  Broker:  "bg-emerald-100 text-emerald-700",
  Carrier: "bg-sky-100 text-sky-700",
  Shipper: "bg-violet-100 text-violet-700",
  All:     "bg-muted text-muted-foreground",
};

export function TopBarV2() {
  const { user, profile, isDemoUser, isAdmin } = useAuth();
  const { openGate } = useLRDirectGate();
  const { guideEnabled, toggleGuide } = useGuide();
  const { openAtlasChat, forceOpenAtlas, sendAtlasMessage, openAtlasDock } = useChatSystem();
  const { toggleMessenger, openMessenger, unreadTotal } = useChatContext();
  const { company, loading: companyLoading } = useCompanyAccount();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [isAsking, setIsAsking] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  // LR Hub-only identity settings dialog (writes to hub_ tables only).
  const [hubSettingsOpen, setHubSettingsOpen] = useState(false);


  const displayName = profile?.first_name && profile?.last_name
    ? `${profile.first_name} ${profile.last_name}`
    : profile?.email?.split("@")[0] || "User";

  const userType = (profile?.user_type as keyof typeof ROLE_TABS | undefined) ?? "unknown";
  const initials = profile?.first_name?.[0]?.toUpperCase() ?? "U";

  const userId = user?.id;


  const handleSignOut = async () => {
    // scope: 'local' — signing out here must kill THIS device only. A bare
    // signOut() defaults to global scope and revokes every device's session.
    logAuthEvent('explicit_signout', { call_site: 'TopBarV2.handleSignOut', scope: 'local' }, userId ?? null);
    await supabase.auth.signOut({ scope: 'local' });
    window.location.href = '/auth?mode=login';
  };

  const isCarrier = userType === "carrier";
  const isShipper = userType === "shipper";
  const isBrokerCarrier = userType === "broker_carrier";

  // Counter-offer badge for carriers / broker_carriers — small query, gated by !!user inside the hook.
  const { offers: carrierOffers } = useCarrierOffers();
  const counterCount = (isCarrier || isBrokerCarrier)
    ? carrierOffers.filter(o => o.status === "countered").length
    : 0;

  // LR Direct providers = brokers, broker_carriers and (Stage 2) carriers.
  const isLRDirectProvider = userType === "broker" || isBrokerCarrier || isCarrier;

  // Unread "bid_won" notifications drive the My Wins nav badge for providers.
  const winsCount = useUnreadWins(isLRDirectProvider ? userId : null);

  // Unread new LR Direct loads (private + open-market) drive the LR Direct nav badge.
  const privateLoadsCount = useUnreadLRDirectLoads(isLRDirectProvider ? userId : null);

  // Toast + badge for providers when a new quote request arrives in one of their networks.
  const { count: brokerQuoteReqCount, markAllRead: markQuoteReqsRead } =
    useUnreadQuoteRequests(isLRDirectProvider ? userId : null);

  // Toast + badge for shippers when a broker submits a quote on their RFQ.
  const { count: shipperQuoteRespCount, markAllRead: markQuoteRespsRead } =
    useUnreadQuoteResponses(isShipper ? userId : null);

  // Shipper realtime: chime + toast when a broker bids on one of their LR Direct loads.
  useDirectBidAlerts(userId, isShipper);

  // Shipper "My Loads" badge: live + in-flight awarded count.
  const shipperMyLoadsCount = useShipperMyLoadsCount(userId, isShipper);

  // Shipper "My Network" badge: pending join requests targeting their company.
  const joinRequestCount = useUnreadJoinRequests(isShipper ? company?.id : null);

  // Provider "My Network" badge: shipper responses (accepted/declined) to join requests.
  const joinResponseCount = useUnreadJoinRequestResponses(
    isLRDirectProvider ? userId : null
  );

  const dashboardHref = isAdmin ? "/broker-dashboard" : isCarrier ? "/carrier-dashboard" : isShipper ? "/shipper-dashboard" : "/broker-dashboard";

  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    const q = searchQuery.trim().slice(0, 500);
    if (!q) return;
    setIsAsking(true);
    openAtlasDock();          // opens floating dock on current page
    sendAtlasMessage(q);      // streams answer into the dock
    setSearchQuery("");
    setTimeout(() => setIsAsking(false), 600);
  }, [searchQuery, openAtlasDock, sendAtlasMessage]);

  // Role-based nav filtering via allow-list. Admins and demo users see everything.
  const allowedHrefs = ROLE_TABS[userType] ?? ROLE_TABS.unknown;

  const { context: productContext, switcherEnabled } = useProductContext();
  const isLRDirectMode = switcherEnabled && productContext === "lr-direct";

  const navItems = NAV_ITEMS
    .map(item => {
      if (item.label === "Dashboard") {
        if (isLRDirectMode && (userType === "broker" || userType === "broker_carrier")) {
          return { ...item, label: "Broker Dashboard", href: "/lr-direct/broker" };
        }
        // Carriers get the same LR Direct dashboard target (label stays neutral).
        if (isLRDirectMode && isCarrier) {
          return { ...item, href: "/lr-direct/broker" };
        }
        if (isShipper && productContext === "lanerunner") {
          return { ...item, href: "/shipper-dashboard-lanerunner" };
        }
        return { ...item, href: dashboardHref };
      }
      // Top-nav "LR Direct" tab lands on the load board for both shippers and
      // brokers. Without this, brokers hit /lr-direct which <Navigate>s to
      // /lr-direct/broker — the same page as the "Broker Dashboard" tab, so
      // the click looks dead. The pill switcher owns the "enter LR Direct"
      // path; this tab is the load board entry point.
      if (item.href === "/lr-direct") return { ...item, href: "/lr-direct/board" };
      return item;
    })
    .filter(item => isAdmin ? true : allowedHrefs.includes(item.href))
    .filter(item => {
      // Carriers (switcherEnabled=false) see all role-allowed items unfiltered.
      if (!switcherEnabled) return true;
      const bucket = navBucketForHref(item.href);
      if (bucket === "shared") return true;
      return bucket === productContext;
    });

  return (
    <>
      <header className="flex flex-col flex-shrink-0 bg-topbar-bg border-b border-topbar-border">
        {/* Top row: logo + search + controls */}
        <div
          className={cn(
            "flex h-14 w-full items-center gap-2 sm:gap-4 px-3 sm:px-5 transition-colors duration-200",
            isLRDirectMode && "bg-amber-50 border-b border-amber-200",
          )}
        >
          {/* Mobile hamburger — top-LEFT, clear of Android system clock top-right */}
          <button
            onClick={() => setMobileNavOpen(true)}
            className="md:hidden relative flex h-9 w-9 items-center justify-center rounded-md text-foreground hover:bg-muted transition-colors flex-shrink-0"
            aria-label={unreadTotal > 0 ? `Open menu, ${unreadTotal} unread messages` : "Open menu"}
          >
            <Menu className="h-5 w-5" />
            {unreadTotal > 0 && (
              <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-danger ring-2 ring-topbar-bg" />
            )}
          </button>

          {/* Logo */}
          <div className="flex items-center flex-shrink-0 mr-2 sm:mr-3">
            <div>
              <img
                src={lanerunnerLogo}
                alt="LaneRunner"
                className="h-7 w-auto object-contain"
              />
            </div>
          </div>


          {/* Product context switcher (hidden on mobile) */}
          <div className="hidden md:flex">
            <ProductContextSwitcher />
          </div>

          {/* ATLAS Search — slimmed so a product-context banner can sit beside it */}
          <form onSubmit={handleSearch} className="hidden sm:block w-full max-w-xs md:max-w-sm flex-shrink min-w-0">
            <div className="relative">
              {isAsking ? (
                <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-brand-teal-deep animate-spin pointer-events-none" />
              ) : (
                <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-brand-teal-deep pointer-events-none" />
              )}
              <Input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Ask Atlas…"
                disabled={isAsking}
                maxLength={500}
                className="pl-9 h-9 text-caption bg-background border-border focus:border-brand-teal-deep focus:ring-1 focus:ring-brand-teal-deep transition-colors"
              />
            </div>
          </form>

          {/* Product-context banner — reads productContext, flips instantly on switch. Hidden < md. */}
          {switcherEnabled && (
            <div
              className={cn(
                "hidden md:inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 border text-[10px] font-bold uppercase tracking-wider whitespace-nowrap flex-shrink-0",
                isLRDirectMode
                  ? "bg-amber-100 text-amber-900 border-amber-300"
                  : "bg-brand-teal-deep/10 text-brand-teal-deep border-brand-teal-deep/30",
              )}
              aria-label="Active product context"
            >
              <span
                className={cn(
                  "h-1.5 w-1.5 rounded-full flex-shrink-0",
                  isLRDirectMode ? "bg-amber-500" : "bg-brand-teal-deep",
                )}
                aria-hidden
              />
              <span>
                {isLRDirectMode ? "LR Direct — Shipper/Broker Bidding" : "LaneRunner — Load Board"}
              </span>
            </div>
          )}


          {/* Right controls */}
          <div className="flex items-center gap-2 ml-auto">
            {/* Company credits */}
            {isDemoUser && (
              <div className="hidden sm:flex items-center gap-1.5">
                <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 border border-amber-300">
                  Demo Mode
                </span>
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-700 border border-blue-300 hover:bg-blue-200 transition-colors">
                      <Info className="h-3 w-3" /> Guide
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-80 p-0">
                    <div className="px-4 py-3 border-b border-border">
                      <p className="text-sm font-bold text-foreground">📋 Feature Guide by Account Type</p>
                    </div>
                    <div className="px-4 py-2 space-y-1.5 max-h-72 overflow-y-auto">
                      {TAB_ROLES.map(({ tab, roles }) => (
                        <div key={tab} className="flex items-center justify-between gap-2 py-1">
                          <span className="text-xs font-semibold text-foreground whitespace-nowrap">{tab}</span>
                          <div className="flex flex-wrap gap-1 justify-end">
                            {roles.map(r => (
                              <span key={r} className={cn("px-1.5 py-0.5 rounded text-[9px] font-bold", ROLE_COLORS[r])}>
                                {r === "All" ? "All Accounts" : r}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="px-4 py-2.5 border-t border-border bg-muted/50">
                      <p className="text-[10px] text-muted-foreground italic">
                        Broker/Carrier combo accounts get both Broker & Carrier features.
                      </p>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            )}
            {/* Guide toggle */}
            <button
              onClick={toggleGuide}
              className={cn(
                "hidden md:flex h-9 w-9 items-center justify-center rounded-md transition-colors",
                guideEnabled
                  ? "bg-brand-teal-deep/10 text-brand-teal-deep"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
              title={guideEnabled ? "Turn off feature hints" : "Turn on feature hints"}
            >
              <GraduationCap className="h-4 w-4" />
            </button>
            <span className="hidden md:inline-flex"><CompanyCreditsBadge /></span>
            <span className="hidden md:inline-flex"><VerifiedBadge documentsVerifiedAt={company?.documents_verified_at ?? null} loading={companyLoading} /></span>
            {/* Quick Post / Post Truck CTA */}
            <DemoHint hintKey="action.quickPost" side="bottom">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(isCarrier ? "/post-truck" : productContext === "lr-direct" ? "/lr-direct/post?mode=load&visibility=private_only" : "/post-load")}
                className="hidden sm:inline-flex h-9 gap-1.5 font-semibold text-caption px-4 text-foreground hover:opacity-85"
                style={{ backgroundColor: "#02ffd3" }}
              >
                {isCarrier ? <Truck className="h-3.5 w-3.5" /> : <PlusCircle className="h-3.5 w-3.5" />}
                {isCarrier ? "POST TRUCK" : "QUICK POST"}
              </Button>
            </DemoHint>

            {/* Atlas Messenger — glass squircle launcher toggles the right-side messages slider.
                Atlas Hub remains reachable via its dedicated nav tab. */}
            <DemoHint hintKey="action.atlasCircle" side="bottom">
              <button
                type="button"
                onClick={toggleMessenger}
                data-messenger-toggle
                className="group relative hidden md:flex h-9 w-9 cursor-pointer transition-transform duration-200 hover:scale-105 active:scale-95"
                title="Messages"
                aria-label={unreadTotal > 0 ? `Messages, ${unreadTotal} unread` : "Messages"}
              >
                {/* Ambient glow */}
                <div className="absolute inset-0 rounded-xl bg-brand-cyan opacity-20 blur-lg transition-opacity group-hover:opacity-40" />

                {/* Glass squircle button body */}
                <div className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-xl border border-white/20 bg-gradient-to-br from-brand-cyan to-brand-teal shadow-[0_6px_12px_-3px_hsl(var(--brand-cyan)/0.35)]">
                  {/* Subtle top shine */}
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/20 to-transparent" />
                  <MessageSquare
                    className="relative h-5 w-5 text-white drop-shadow-sm transition-transform duration-200 group-hover:-rotate-3"
                    strokeWidth={2.5}
                  />
                </div>

                {unreadTotal > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-danger text-white text-[10px] font-bold flex items-center justify-center ring-1 ring-background tabular-nums z-10">
                    {unreadTotal > 99 ? "99+" : unreadTotal}
                  </span>
                )}

              </button>
            </DemoHint>




            {/* Settings / User dropdown — desktop only */}
            <div className="hidden md:flex">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className={cn(
                  "flex items-center gap-2 rounded-md px-2 py-1.5 h-9",
                  "text-caption font-medium text-foreground hover:bg-muted transition-colors border border-border"
                )}>
                  <UserAvatar
                    path={profile?.avatar_url}
                    name={displayName}
                    email={profile?.email}
                    size="sm"
                  />
                  <span className="hidden sm:inline max-w-[100px] truncate font-semibold text-foreground">
                    {displayName}
                  </span>
                  <span className="hidden sm:inline rounded px-1.5 py-0.5 text-[10px] font-semibold bg-success-subtle text-success capitalize">
                    {String(userType)}
                  </span>
                  <ChevronDown className="h-3 w-3 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem asChild className="gap-2 text-caption">
                  <Link to="/profile-settings"><Settings className="h-3.5 w-3.5" /> Account Settings</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="gap-2 text-caption">
                  <Link to="/company-settings"><Building2 className="h-3.5 w-3.5" /> Company</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="gap-2 text-caption">
                  <Link to="/billing"><CreditCard className="h-3.5 w-3.5" /> Billing & Account</Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="gap-2 text-caption"
                  onSelect={(e) => { e.preventDefault(); setHubSettingsOpen(true); }}
                >
                  <Users className="h-3.5 w-3.5" /> LR Hub Settings
                </DropdownMenuItem>

                <DropdownMenuSeparator />
                <DropdownMenuItem asChild className="gap-2 text-caption">
                  <Link to="/privacy"><Shield className="h-3.5 w-3.5" /> Privacy Policy</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="gap-2 text-caption">
                  <Link to="/terms"><FileText className="h-3.5 w-3.5" /> Terms of Service</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="gap-2 text-caption">
                  <Link to="/cancellation-policy"><RefreshCcw className="h-3.5 w-3.5" /> Cancellation Policy</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="gap-2 text-caption">
                  <Link to="/cookies"><Cookie className="h-3.5 w-3.5" /> Cookie Policy</Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="gap-2 text-caption"
                  onSelect={(e) => {
                    e.preventDefault();
                    window.dispatchEvent(new Event('lr-open-cookie-settings'));
                  }}
                >
                  <Cookie className="h-3.5 w-3.5" /> Cookie Preferences
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="gap-2 text-caption"
                  onSelect={(e) => { e.preventDefault(); setSupportOpen(true); }}
                >
                  <LifeBuoy className="h-3.5 w-3.5" /> Support / Report an Issue
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="gap-2 text-caption">
                  <Link to="/safety-report"><ShieldAlert className="h-3.5 w-3.5" /> Report or Block User</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="gap-2 text-caption text-danger focus:text-danger" onClick={handleSignOut}>
                  <LogOut className="h-3.5 w-3.5" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </div>

            {/* Menu Sheet — trigger lives at top-LEFT (see start of row); this only renders the drawer content. */}
            <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>

              <SheetContent side="right" className="w-[88vw] max-w-[340px] p-0 overflow-y-auto">
                <SheetHeader className="px-4 py-3 border-b">
                  <SheetTitle className="text-left text-base flex items-center gap-2">
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-teal-deep text-white text-xs font-bold">
                      {initials}
                    </div>
                    <span className="truncate">{displayName}</span>
                  </SheetTitle>
                </SheetHeader>
                <div className="px-4 py-3 space-y-4">
                  {/* Messages — mobile access to the messenger (desktop uses the top-right icon) */}
                  <button
                    onClick={() => { setMobileNavOpen(false); openMessenger(); }}
                    className="w-full flex items-center gap-3 px-2 py-2.5 text-sm rounded-md hover:bg-muted text-left"
                  >
                    <MessageSquare className="h-4 w-4 text-brand-teal-deep" />
                    <span className="flex-1 font-medium">Messages</span>
                    {unreadTotal > 0 && (
                      <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-danger text-white text-[10px] font-bold flex items-center justify-center tabular-nums">
                        {unreadTotal > 99 ? "99+" : unreadTotal}
                      </span>
                    )}
                  </button>

                  {/* Product switcher */}
                  {switcherEnabled && (
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Product</div>
                      <ProductContextSwitcher />
                    </div>
                  )}

                  {/* Verified status */}
                  <div className="border-t pt-3">
                    <VerifiedBadge documentsVerifiedAt={company?.documents_verified_at ?? null} loading={companyLoading} />
                  </div>

                  {/* Credits / Boost */}
                  <div className="border-t pt-3">
                    <CompanyCreditsBadge />
                  </div>

                  {/* Helper toggle */}
                  <div className="flex items-center justify-between border-t pt-3">
                    <div className="flex items-center gap-2">
                      <GraduationCap className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">Feature hints</span>
                    </div>
                    <button
                      onClick={toggleGuide}
                      className={cn(
                        "h-7 px-3 rounded-full text-xs font-semibold transition-colors",
                        guideEnabled ? "bg-brand-teal-deep text-white" : "bg-muted text-muted-foreground"
                      )}
                    >
                      {guideEnabled ? "On" : "Off"}
                    </button>
                  </div>

                  {/* Account links */}
                  <div className="border-t pt-3 flex flex-col">
                    <Link to="/profile-settings" onClick={() => setMobileNavOpen(false)} className="flex items-center gap-2 px-2 py-2.5 text-sm rounded-md hover:bg-muted">
                      <User className="h-4 w-4" /> Profile
                    </Link>
                    <Link to="/company-settings" onClick={() => setMobileNavOpen(false)} className="flex items-center gap-2 px-2 py-2.5 text-sm rounded-md hover:bg-muted">
                      <Building2 className="h-4 w-4" /> Company Settings
                    </Link>
                    <Link to="/profile-settings" onClick={() => setMobileNavOpen(false)} className="flex items-center gap-2 px-2 py-2.5 text-sm rounded-md hover:bg-muted">
                      <Settings className="h-4 w-4" /> Settings
                    </Link>
                    <Link to="/billing" onClick={() => setMobileNavOpen(false)} className="flex items-center gap-2 px-2 py-2.5 text-sm rounded-md hover:bg-muted">
                      <CreditCard className="h-4 w-4" /> Billing & Account
                    </Link>
                    <button
                      onClick={() => { setMobileNavOpen(false); setSupportOpen(true); }}
                      className="flex items-center gap-2 px-2 py-2.5 text-sm rounded-md hover:bg-muted text-left"
                    >
                      <LifeBuoy className="h-4 w-4" /> Support / Report an Issue
                    </button>
                    <button
                      onClick={() => { setMobileNavOpen(false); handleSignOut(); }}
                      className="flex items-center gap-2 px-2 py-2.5 text-sm rounded-md text-danger hover:bg-danger/10 text-left"
                    >
                      <LogOut className="h-4 w-4" /> Sign out
                    </button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Horizontal nav tab bar */}
        <nav className="flex items-center gap-0 px-3 sm:px-5 border-t border-topbar-border bg-topbar-bg overflow-x-auto [scrollbar-width:thin] [-webkit-overflow-scrolling:touch]">
          {navItems.map(item => {
            const fromMyWins = (location.state as { from?: string } | null)?.from === "my-wins";
            const onOpportunity = location.pathname.startsWith("/lr-direct/opportunity");
            const onLiveRoom = location.pathname.startsWith("/lr-direct/live");
            const isMyOffers = item.href === "/search?tab=my-offers";
            const isShipperMyLoads = item.href === "/lr-direct/my-loads";
            const isActive = item.href === "/lr-direct/my-wins"
              ? location.pathname === "/lr-direct/my-wins" || (onOpportunity && fromMyWins)
              : isShipperMyLoads
                ? location.pathname === "/lr-direct/my-loads"
                  || onLiveRoom
                  || (onOpportunity && !fromMyWins)
                : item.href === "/lr-direct"
                  ? (location.pathname === "/lr-direct" || location.pathname === "/lr-direct/board")
                    && !onLiveRoom
                    && !(onOpportunity && fromMyWins)
                  : isMyOffers
                    ? location.pathname === "/search" && location.search.includes("tab=my-offers")
                    : item.href === "/search"
                      ? location.pathname === "/search" && !location.search.includes("tab=my-offers")
                      : location.pathname === item.href;
            const isBrokerQuotes = item.href === "/lr-direct/broker-quotes";
            const isShipperQuotes = item.href === "/lr-direct/my-quotes";
            const isLrDirectTop = item.href === "/lr-direct";
            return (
              <DemoHint hintKey={item.hintKey} side="bottom" key={item.href}>
                <Link
                  to={item.href}
                  onClick={(e) => {
                    if (item.href.startsWith("/lr-direct")) openGate(e);
                    if (isBrokerQuotes) markQuoteReqsRead();
                    if (isShipperQuotes) markQuoteRespsRead();
                    if (isLrDirectTop && userId && privateLoadsCount > 0) {
                      markLRDirectLoadsRead(userId).catch(() => { /* noop */ });
                    }
                  }}
                  className={cn(
                    "relative px-4 py-2.5 text-[11px] font-bold uppercase tracking-widest transition-colors whitespace-nowrap border-b-2 -mb-px",
                    isActive
                      ? "border-brand-teal-deep text-brand-teal-deep"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  )}
                >
                  {item.label}
                  {isMyOffers && counterCount > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-danger text-white text-[9px] font-bold align-middle">
                      {counterCount}
                    </span>
                  )}
                  {item.href === "/lr-direct/my-wins" && winsCount > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-danger text-white text-[9px] font-bold align-middle">
                      {winsCount > 9 ? "9+" : winsCount}
                    </span>
                  )}
                  {isLrDirectTop && privateLoadsCount > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-danger text-white text-[9px] font-bold align-middle">
                      {privateLoadsCount > 9 ? "9+" : privateLoadsCount}
                    </span>
                  )}
                  {isBrokerQuotes && brokerQuoteReqCount > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-danger text-white text-[9px] font-bold align-middle">
                      {brokerQuoteReqCount > 9 ? "9+" : brokerQuoteReqCount}
                    </span>
                  )}
                  {isShipperQuotes && shipperQuoteRespCount > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-danger text-white text-[9px] font-bold align-middle">
                      {shipperQuoteRespCount > 9 ? "9+" : shipperQuoteRespCount}
                    </span>
                  )}
                  {isShipperMyLoads && isShipper && shipperMyLoadsCount > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-brand-teal-deep text-white text-[9px] font-bold align-middle">
                      {shipperMyLoadsCount > 9 ? "9+" : shipperMyLoadsCount}
                    </span>
                  )}
                  {item.href === "/lr-direct/network-settings" && isShipper && joinRequestCount > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-danger text-white text-[9px] font-bold align-middle">
                      {joinRequestCount > 9 ? "9+" : joinRequestCount}
                    </span>
                  )}
                  {item.href === "/lr-direct/my-network" && joinResponseCount > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-danger text-white text-[9px] font-bold align-middle">
                      {joinResponseCount > 9 ? "9+" : joinResponseCount}
                    </span>
                  )}
                </Link>
              </DemoHint>
            );
          })}
        </nav>
      </header>

      <SupportTicketModal open={supportOpen} onOpenChange={setSupportOpen} />
      <HubSettingsDialog open={hubSettingsOpen} onOpenChange={setHubSettingsOpen} />

    </>

  );
}
